import React, { Component } from 'react';

import Announcements from './Announcements';
import AnnouncementModal from './components/modals/AnnouncementModal';
import Constants from './Constants';
import Seasons from './Seasons';
import ControlPanel from './components/ControlPanel';
import DatePanel from './components/DatePanel';
import FeedbackModal from './components/modals/FeedbackModal';
import Footer from './components/Footer';
import Game from './components/Game';
import Header from './components/Header';
import LeaderboardModal from './components/modals/LeaderboardModal';
import LoginModal from './components/modals/LoginModal';
import ModePanel from './components/ModePanel';
import ProfileModal from './components/modals/ProfileModal';
import RulesModal from './components/modals/RulesModal';
import RulesPanel from './components/RulesPanel';
import ScheduleModal from './components/modals/ScheduleModal';
import Scoreboard from './components/Scoreboard';
import SeasonWelcomeModal from './components/modals/SeasonWelcomeModal';
import Typography from './Typography';
import Utils from './Utils';

import './App.css';

class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      authToken: "",
      loggedInPlayerName: null,
      profile: null,
      dailyAttempted: Constants.ATTEMPT_NONE,
      dailyScore: null,
      sevenDayScoreString: null,
      gameKey: null,
      liveGame: false,
      daily: true,
      rows: Constants.DEFAULT_ROWS,
      columns: Constants.DEFAULT_COLUMNS,
      startLetterCount: 0,
      holdCount: 0,
      previewCount: 0,
      expansionCount: 0,
      bonusCellCount: 0,
      bonusCells: [],
      adjacencyMode: false,
      incrementMode: false,
      dropMode: false,
      slideMode: false,
      wraparoundMode: false,
      letterOptionsPerRound: 0,
      letterOptionString: null,
      scores: {},
      visibleModal: Constants.MODAL_NONE,
      // The announcement on screen, and whether announcements are being shown
      // unprompted -- in which case closing one moves on to the next.
      announcement: null,
      announcing: false,
      spectating: null,
      yesterdayStarMap: {},
      lastWeekMedalMap: {},
      lastSeasonTrophyMap: {},
      profileSeasons: [],
      profileMedals: [],
      profileStreaks: Constants.NO_STREAKS,
      boardString: null,
      elapsedTurns: 0,
      holdString: "",
      // Applied from the cached hint before the first paint; replaced by the
      // account's own value as soon as the profile arrives.
      typography: Typography.apply(Typography.readHint()),
      typographyError: null
    };
    this.state.scores[Constants.SCORES_TODAY] = {};
    this.state.scores[Constants.SCORES_YESTERDAY] = {};
    this.state.scores[Constants.SCORES_THIS_WEEK] = {};
    this.state.scores[Constants.SCORES_LAST_WEEK] = {};
    this.state.scores[Constants.SCORES_THIS_SEASON] = {};
    this.state.scores[Constants.SCORES_LAST_SEASON] = {};

    this.controlPanel = React.createRef();
    this.currentScoresRequest = 0;
  }

  componentDidMount() {
    this.fetchIdentity();
    this.fetchPastScores();

    window.addEventListener('beforeunload', (e) => {
      if (this.state.liveGame) {
        e.preventDefault();
        e.returnValue = "You have a game in progress.  Are you sure you want to exit?";
      }
    });
  }

  fetchIdentity = async () => {
    const tokenData = await fetch(`/api/checkToken?token=${this.state.authToken}`);
    const tokenDataJson = await tokenData.json();
    if (tokenDataJson.success) {
      const authToken = tokenDataJson.data.token;
      const loggedInPlayerName = tokenDataJson.data.userName;
      const attempt = await this.getAttempt(loggedInPlayerName);
      const dailyAttempted = attempt.attempt;
      const dailyScore = attempt.score;
      const sevenDayScoreString = await this.fetchSevenDayScoreString(loggedInPlayerName);
      const profile = await this.getProfile(loggedInPlayerName);
      const profileSeasons = await this.getSeasonResults(loggedInPlayerName);
      const profileMedals = await this.getWeeklyMedals(loggedInPlayerName);
      const profileStreaks = await this.getStreaks(loggedInPlayerName);

      // The account is the only source of truth for the setting. Writing the
      // hint from the server's answer -- never the reverse -- is what keeps a
      // stale or hand-edited local value from surviving past this point. A
      // profile that failed to load is not an answer, so it leaves both alone
      // rather than recording a guess the player would then have to undo.
      let typography = this.state.typography;
      if (profile) {
        typography = Typography.apply(profile.typography);
        Typography.writeHint(typography);
      }

      this.setState({
        authToken,
        loggedInPlayerName,
        dailyAttempted,
        dailyScore,
        sevenDayScoreString,
        profile,
        profileSeasons,
        profileMedals,
        profileStreaks,
        typography,
        typographyError: null
      });

      this.fetchCurrentScores(dailyAttempted === Constants.ATTEMPT_COMPLETE);
    } else {
      this.setState({
        authToken: "",
        loggedInPlayerName: null,
        dailyAttempted: Constants.ATTEMPT_NONE,
        dailyScore: null,
        sevenDayScoreString: null,
        profile: null,
        profileSeasons: [],
        profileMedals: [],
        profileStreaks: Constants.NO_STREAKS,
        // The setting belongs to an account, so signing out returns the site to
        // the default rather than leaving the last player's choice behind on a
        // shared machine.
        typography: Typography.apply(Constants.TYPOGRAPHY_MODERN),
        typographyError: null
      });
      Typography.clearHint();

      // Signing out closes today's boards again, the same way signing in opens
      // them: they belong to a player who has played, not to the browser.
      this.fetchCurrentScores(false);
    }
  }

  // The two fetches below each own half the board, so each writes only its own
  // periods and leaves the other half standing.
  mergeScores = (periods) => {
    this.setState((state) => ({ scores: { ...state.scores, ...periods } }));
  }

  // The scoreboard is fetched in two halves, because its two halves are open on
  // different terms. The finished periods are public and arrive on their own;
  // the ones still being played into are today's results, and the server hands
  // those over only to a player who has finished their own daily.
  //
  // This is the public half: yesterday, last week, last season. It does not wait
  // on identity, so the panel fills for a visitor who is not signed in, and
  // fills without waiting out the six requests the profile takes. It also
  // carries the accolades, which are awarded on the previous period of each span
  // and so are read off exactly these three boards.
  //
  // No titles in either half: the scoreboard's own selectors name the period
  // they have chosen, and the season ones name it by number from the day they
  // are drawn on, so a title assembled here would be a second copy of the season
  // number built at a different moment.
  fetchPastScores = async () => {
    const previousSeasonNumber = Seasons.getCurrentSeasonNumber() - 1;

    const scoresYesterday = await App.fetchScoresForDate(Utils.getDateStringYesterday());
    const scoresLastWeek = await App.fetchScoresForWeek(Utils.getDateStringDaysAgo(7));
    const scoresLastSeason = await App.fetchScoresForSeason(previousSeasonNumber);

    this.mergeScores({
      [Constants.SCORES_YESTERDAY]: App.assemblePeriod(scoresYesterday),
      [Constants.SCORES_LAST_WEEK]: App.assemblePeriod(scoresLastWeek),
      // A season the site has not reached yet is marked unavailable rather than
      // shown empty, so the scoreboard offers it disabled instead of carrying a
      // dead panel for the six weeks before there is a previous season to show.
      [Constants.SCORES_LAST_SEASON]: App.assemblePeriod(scoresLastSeason, previousSeasonNumber < 1)
    });

    this.setState({
      yesterdayStarMap: Utils.getAccoladeMap(scoresYesterday),
      lastWeekMedalMap: Utils.getAccoladeMap(scoresLastWeek),
      lastSeasonTrophyMap: Utils.getAccoladeMap(scoresLastSeason)
    });
  }

  // Today, this week, this season -- the three that include today, and so the
  // three the server withholds until the daily is played. `fetchIdentity` is
  // what knows the answer, so it is what calls this, on every path that can
  // change it: the first load, a login, a logout, and the moment a daily is
  // recorded.
  //
  // A locked period is marked rather than left empty: the board has to say why
  // it is blank, and an empty panel says nothing. None of the boards is
  // requested in that case -- the refusal is the server's to make, but there is
  // no sense asking for three boards we already know will be refused.
  fetchCurrentScores = async (unlocked) => {
    const request = ++this.currentScoresRequest;
    const seasonNumber = Seasons.getCurrentSeasonNumber();

    if (!unlocked) {
      const counts = await App.fetchCurrentPeriodCounts();
      if (request !== this.currentScoresRequest) {
        return;
      }

      this.mergeScores({
        [Constants.SCORES_TODAY]: App.assembleLockedPeriod(counts[Constants.SCORES_TODAY]),
        [Constants.SCORES_THIS_WEEK]: App.assembleLockedPeriod(counts[Constants.SCORES_THIS_WEEK]),
        [Constants.SCORES_THIS_SEASON]: App.assembleLockedPeriod(
          counts[Constants.SCORES_THIS_SEASON], seasonNumber < 1)
      });
      return;
    }

    const scoresToday = await App.fetchScoresForDate(Utils.getDateStringToday());
    const scoresThisWeek = await App.fetchScoresForWeek(Utils.getDateStringToday());
    const scoresThisSeason = await App.fetchScoresForSeason(seasonNumber);
    if (request !== this.currentScoresRequest) {
      return;
    }

    this.mergeScores({
      [Constants.SCORES_TODAY]: App.assemblePeriod(scoresToday),
      [Constants.SCORES_THIS_WEEK]: App.assemblePeriod(scoresThisWeek),
      [Constants.SCORES_THIS_SEASON]: App.assemblePeriod(scoresThisSeason, seasonNumber < 1)
    });
  }

  getAttempt = async (playerName) => {
    const date = Utils.getDateStringToday();

    const gameData = await fetch(`/api/games/date/${date}/player/${playerName}`);
    const gameDataJson = await gameData.json();
    if (gameDataJson.data.length >= 1) {
      return {
        attempt: Constants.ATTEMPT_COMPLETE,
        score: gameDataJson.data[0].totalScore + gameDataJson.data[0].holdPenalty
      };
    }

    const data = await fetch(`/api/attempts/date/${date}/player/${playerName}`);
    const dataJson = await data.json();
    if (dataJson.data.length >= 1) {
      return {
        attempt: Constants.ATTEMPT_INCOMPLETE,
        score: null
      };
    }

    return {
      attempt: Constants.ATTEMPT_NONE,
      score: null
    };
  }

  fetchSevenDayScoreString = async (playerName) => {
    const date = Utils.getDateStringMostRecentMonday();
    const scoreData = await fetch(`/api/games/date/${date}/player/${playerName}/dayCount/7`);
    const scoreDataJson = await scoreData.json();

    const scoreMap = {};
    let totalScore = 0;
    scoreDataJson.data.filter(data => {
      return typeof data.valid !== 'boolean' || data.valid === true;
    }).forEach(data => {
      const score = data.totalScore + (data.holdPenalty || 0);
      scoreMap[data.date] = score;
      totalScore += score;
    });

    let scoreString = Utils.assembleScoreHistoryString(scoreMap, Utils.getDateStringMostRecentMonday());
    scoreString += ` | ${totalScore}`;

    return scoreString;
  }

  getProfile = async (playerName) => {
    const playerData = await fetch(`/api/account/${playerName}`);
    const playerDataJson = await playerData.json();
    return playerDataJson.data;
  }

  getSeasonResults = async (playerName) => {
    const seasonData = await fetch(`/api/seasons/player/${playerName}`);
    const seasonDataJson = await seasonData.json();
    return seasonDataJson.success ? seasonDataJson.data : [];
  }

  getWeeklyMedals = async (playerName) => {
    const medalData = await fetch(`/api/weeks/player/${playerName}/medals`);
    const medalDataJson = await medalData.json();
    return medalDataJson.success ? medalDataJson.data.medals : [];
  }

  getStreaks = async (playerName) => {
    const streakData = await fetch(`/api/streaks/player/${playerName}`);
    const streakDataJson = await streakData.json();
    return streakDataJson.success ? streakDataJson.data : Constants.NO_STREAKS;
  }

  // A period as the scoreboard reads it. It shows the top ten, but keeps the
  // whole ranked field behind them, which is what lets it tell a logged-in
  // player where they stand when they are not on the board.
  static assemblePeriod = (scores, unavailable) => {
    const standings = App.rankScores(scores);

    return {
      scores: App.sliceScores(standings, 10),
      standings,
      gameCount: scores.length,
      unavailable
    };
  }

  static assembleLockedPeriod = (gameCount, unavailable) => {
    return { ...Constants.LOCKED_PERIOD, gameCount, unavailable };
  }

  // Ranks a field already sorted best first, with ties sharing a rank.
  static rankScores = (scores) => {
    let currentScore = -1;
    let rank = -1;

    return scores.map((score, index) => {
      if (score.score !== currentScore) {
        currentScore = score.score;
        rank = index + 1;
      }

      return {
        rank: rank,
        name: score.name,
        score: score.score,
        games: score.games,
        bestScore: score.bestScore,
        countedByDayOfWeek: score.countedByDayOfWeek,
        history: score.history
      };
    });
  }

  // The top ten, never cutting a tie in half: everybody level with the
  // tenth-place score stays on the board, however many that is.
  static sliceScores = (rankedScores, minCount) => {
    if (rankedScores.length === 0) {
      return [];
    }

    const borderScore = rankedScores[Math.min(minCount, rankedScores.length) - 1].score;

    return rankedScores.filter((score) => score.score >= borderScore);
  }

  static fetchCurrentPeriodCounts = async () => {
    return fetch('/api/scoreboard/counts')
      .then(data => data.json())
      .then(res => res.success ? res.data : {})
      .catch(() => ({}));
  }

  static fetchScoresForDate = async (date) => {
    return fetch(`/api/games/date/${date}`)
      .then(data => data.json())
      .then((res) => {
        const scores = [];

        for (let i = 0; i < res.data.length; i++) {
          const valid = res.data[i].valid;
          if (typeof valid === 'boolean' && valid === false) {
            continue;
          }

          const holdPenalty = res.data[i].holdPenalty || 0;
          scores.push({
            name: res.data[i].playerName,
            score: res.data[i].totalScore + holdPenalty,
            games: 1
          });
        }

        scores.sort((a, b) => {
          return b.score - a.score;
        });

        return scores;
      });
  }

  // Season standings are ranked by the server, which is the only place a
  // season is aggregated: a finished season is served from the rows frozen
  // when it was settled, so a trophy shown here matches the one on the profile.
  static fetchScoresForSeason = async (seasonNumber) => {
    if (seasonNumber < 1) {
      return [];
    }

    const data = await fetch(`/api/seasons/${seasonNumber}/standings`);
    const dataJson = await data.json();

    return dataJson.success ? dataJson.data.standings : [];
  }

  static fetchScoresForWeek = async (date) => {
    const monday = Utils.getDateStringMonday(date);

    const data = await fetch(`/api/games/date/${monday}/dayCount/7`);
    const dataJson = await data.json();
    const scoreMap = {};

    for (let i = 0; i < dataJson.data.length; i++) {
      const valid = dataJson.data[i].valid;
      if (typeof valid === 'boolean' && valid === false) {
        continue;
      }

      const playerName = dataJson.data[i].playerName;
      if (!(playerName in scoreMap)) {
        scoreMap[playerName] = {
          score: 0,
          games: 0,
          history: {}
        }
      }
      const holdPenalty = dataJson.data[i].holdPenalty || 0;
      const score = dataJson.data[i].totalScore + holdPenalty;

      // Add scores if this isn't April Fools day 2022 (French).
      if (dataJson.data[i].date !== Constants.EXCLUDED_SCORE_DATE) {
        scoreMap[playerName].score += score;
        scoreMap[playerName].games++;
        scoreMap[playerName].history[dataJson.data[i].date] = score;
      }
    }

    const scoreArray = [];
    for (let playerName in scoreMap) {
      scoreArray.push({
        name: playerName,
        score: scoreMap[playerName].score,
        games: scoreMap[playerName].games,
        history: scoreMap[playerName].history
      });
    }

    scoreArray.sort((a, b) => {
      return b.score - a.score;
    });

    return scoreArray;
  }

  // How much of the day's letter sequence is revealed is decided by the server
  // from the player's recorded attempt, so there is nothing to pass here.
  static fetchDailyGameData = async () => {
    return fetch('/api/dailyGameData')
      .then(data => data.json())
      .then(res => res.success ? res.data : null);
  }

  static fetchVersion = async () => {
    return fetch('/api/version')
      .then(data => data.json())
      .then(res => res.success ? res.data.version : null)
      .catch(() => null);
  }

  // Whether this bundle is the one the server is currently serving. A version
  // that could not be read counts as current: this guard exists to keep a stale
  // tab from opening a board it has no code to draw, and locking every player
  // out of the daily because the check itself broke would be a far worse
  // failure than the one it prevents. It fails open on purpose.
  static isBundleCurrent = async () => {
    const version = await App.fetchVersion();

    return !version || version === Constants.VERSION;
  }

  static startDailyAttempt = async () => {
    return fetch('/api/attempts/start', { method: 'POST' })
      .then(data => data.json())
      .then(res => res.success);
  }

  handleScoreboardClick = (playerName, date) => {
    this.handleStartButtonClick(Constants.GAME_TYPE_DAILY, false, playerName, date, Constants.SPECTATOR_PARAMETERS);
  }

  // The day's shape travels as one object rather than a dozen positional
  // arguments: it is the very shape `Seasons.getDailyParameters` returns and
  // the server hands back, so adding a twist is a field rather than another
  // argument every caller has to place correctly.
  //
  // Returns an error string when the game could not be opened and nothing when
  // it could, so the panel the player is looking at can say why. Callers that
  // have no panel to say it on -- spectating from the scoreboard, recovering
  // from a failed save -- ignore it.
  handleStartButtonClick = async (gameType, liveGame, spectating, date, parameters) => {
    const gameKey = Date.now();
    const daily = (gameType === Constants.GAME_TYPE_DAILY);
    const boardString = null;
    const elapsedTurns = 0;
    let letterOptionString;
    let startLetters;
    let expansions;
    let bonusCells;

    if (daily && spectating) {
      // The board is loaded from the recorded game; today's parameters are irrelevant.
      letterOptionString = null;
      startLetters = {};
      expansions = [];
      bonusCells = [];
    } else if (daily) {
      // Checked before the attempt is seeded, so a start this refuses leaves
      // nothing behind: the player reloads onto the current bundle and meets the
      // same fresh daily rather than a phantom game to continue. It cannot be
      // folded into the fetch below either -- that one has to come after the
      // attempt is cleared, or a "Try again!" would be dealt its reveal against
      // the finished game's turn count.
      if (!await App.isBundleCurrent()) {
        return Constants.ERROR_VERSION_STALE;
      }

      // The server refuses to clear progress on a daily that is already under
      // way, so a refused start means there is a game to continue instead.
      if (!await App.startDailyAttempt()) {
        // The daily is already under way; re-reading identity swaps the panel
        // over to "Continue!" instead of leaving a button that does nothing.
        this.fetchIdentity();
        return;
      }

      const data = await App.fetchDailyGameData();
      if (!data) {
        return;
      }

      letterOptionString = data.gameData.letterOptions;
      startLetters = data.gameData.startLetters;
      expansions = data.gameData.expansions;
      bonusCells = data.gameData.bonusCells || [];

      // The board is drawn from the parameters the day was dealt with, which
      // the server holds, rather than from the schedule this bundle happens to
      // carry. A deploy part-way through a day would otherwise leave the two
      // disagreeing and every placement rejected.
      parameters = data.parameters;
    } else {
      const rounds = Seasons.getRoundCount(parameters);
      letterOptionString = Utils.generateRandomLetterOptionString(
        rounds, parameters.incrementMode ? 3 : parameters.letterOptionsPerRound);
      startLetters = Utils.generateRandomStartLetters(
        parameters.rows, parameters.columns, parameters.startLetterCount);
      expansions = Utils.generateRandomCoordinates(
        parameters.rows, parameters.columns, parameters.expansionCount, Object.keys(startLetters));
      // As on the server: a cell wears one marking, so the gold cells keep off
      // the start letters and off the green ones.
      bonusCells = Utils.generateRandomCoordinates(
        parameters.rows, parameters.columns, parameters.bonusCellCount,
        [...Object.keys(startLetters), ...expansions]);
    }

    this.setState({
      gameKey, liveGame, date, daily, ...parameters,
      startLetters, expansions, bonusCells, letterOptionString, spectating, boardString, elapsedTurns, holdString: ""
    });

    // The daily is the one screen every player passes through, so it is where
    // announcements are made. A spectator is watching somebody else's board and
    // is not starting anything.
    if (daily && !spectating) {
      this.showNextAnnouncement();
    }
  }

  handleContinueButtonClick = async (boardString, elapsedTurns, holdString, playedStartLetters) => {
    // A resume is gated exactly as a fresh start is: the board a stale bundle
    // would rebuild is the same board it cannot draw. `handleSaveFailure` comes
    // through here too and is right to be refused as well -- the move it is
    // recovering from was rejected because this bundle is stale, and the message
    // already on screen tells the player to reload.
    if (!await App.isBundleCurrent()) {
      return Constants.ERROR_VERSION_STALE;
    }

    const gameKey = Date.now();
    const liveGame = true;
    const date = Utils.getDateStringToday();
    const daily = true;
    const data = await App.fetchDailyGameData();
    if (!data) {
      return;
    }

    // As on a fresh start, the day's shape comes from the server's record of
    // how it was dealt.
    const parameters = data.parameters;
    const letterOptionString = data.gameData.letterOptions;
    // On a slide day the given letters have moved, so the attempt's record of
    // where they are now beats the day's record of where they were dealt. Every
    // other day the two agree, and a game recorded before this was stored has
    // no copy of its own and falls back to the deal.
    const startLetters = playedStartLetters && Object.keys(playedStartLetters).length > 0
      ? playedStartLetters
      : data.gameData.startLetters;
    const expansions = data.gameData.expansions;
    const bonusCells = data.gameData.bonusCells || [];
    const spectating = false;

    this.setState({
      gameKey, liveGame, date, daily, ...parameters,
      startLetters, expansions, bonusCells, letterOptionString, spectating, boardString, elapsedTurns, holdString
    });
  }

  // Reloads the in-progress daily from the server after a move the server did
  // not accept, so play resumes from what was actually recorded.
  handleSaveFailure = async () => {
    if (!this.state.daily || !this.state.loggedInPlayerName) {
      return;
    }

    try {
      const date = Utils.getDateStringToday();
      const data = await fetch(`/api/attempts/date/${date}/player/${this.state.loggedInPlayerName}`);
      const dataJson = await data.json();
      if (!dataJson.success || dataJson.data.length !== 1) {
        return;
      }

      const attempt = dataJson.data[0];
      this.handleContinueButtonClick(
        attempt.boardString, attempt.elapsedTurns, attempt.holdString, attempt.startLetters);
    } catch (error) {
      // Leave the error on screen; the message tells the player to reload.
    }
  }

  handleGameFinish = () => {
    // Only the periods that include today can have changed, and re-reading
    // identity is what fetches those -- it is also what unlocks them, since the
    // game just recorded is the one the server was waiting for.
    this.fetchIdentity();

    this.setState({
      liveGame: false
    });
  }

  handleQuitButtonClick = () => {
    this.setState({
      gameKey: null,
      liveGame: false
    });
  }

  handleModalClose = () => {
    if (this.state.announcing && this.showNextAnnouncement()) {
      return;
    }

    this.setState({
      visibleModal: Constants.MODAL_NONE,
      announcement: null,
      announcing: false
    });
  }

  handleScheduleClick = () => {
    this.setState({
      visibleModal: Constants.MODAL_SCHEDULE
    });
  }

  // The live announcements this player has not seen yet. Seen is recorded on
  // the account, so somebody signed out has none -- they cannot start a daily
  // either, which is where these are shown.
  getPendingAnnouncements = () => {
    if (!this.state.profile) {
      return [];
    }

    return Announcements.getPending(
      Utils.getDateStringToday(), this.state.profile.seenAnnouncements || []);
  }

  // Shows the first pending announcement and records it, returning whether
  // there was one. Several pending at once are shown one after another, each
  // as the last is closed, in the order Constants.ANNOUNCEMENTS lists them.
  showNextAnnouncement = () => {
    const announcement = this.getPendingAnnouncements()[0];
    if (!announcement) {
      return false;
    }

    this.setState({
      visibleModal: announcement.modal || Constants.MODAL_ANNOUNCEMENT,
      announcement,
      announcing: true
    });
    this.recordAnnouncementSeen(announcement.id);
    return true;
  }

  // Recorded on the account, and locally at once so a second daily in the same
  // session cannot show it twice while the write is in flight. A write that
  // fails is left alone: the cost is being shown it once more on the next load,
  // which is the harmless direction for this to fail in and not worth an error
  // message over something the player has already read.
  recordAnnouncementSeen = async (id) => {
    if (!this.state.profile || (this.state.profile.seenAnnouncements || []).includes(id)) {
      return;
    }

    this.setState((state) => ({
      profile: {
        ...state.profile,
        seenAnnouncements: [...(state.profile.seenAnnouncements || []), id]
      }
    }));

    try {
      await fetch('/api/account/seenAnnouncement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
    } catch (err) {
      // Deliberately swallowed; see above.
    }
  }

  handleTypographyChange = async (typography) => {
    if (!Typography.isValid(typography) || typography === this.state.typography) {
      return;
    }

    // Applied first: the point of the setting is to see it, and a failed save
    // is reported rather than silently leaving the page in the old face.
    const previous = this.state.typography;
    Typography.apply(typography);
    this.setState({ typography, typographyError: null });

    try {
      const data = await fetch('/api/account/typography', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ typography }),
      });
      const dataJson = await data.json();
      if (!dataJson.success) {
        throw new Error(dataJson.error);
      }
    } catch (error) {
      Typography.apply(previous);
      this.setState({
        typography: previous,
        typographyError: Constants.ERROR_TYPOGRAPHY_NOT_SAVED
      });
      return;
    }

    Typography.writeHint(typography);
    this.setState({
      profile: this.state.profile ? { ...this.state.profile, typography } : this.state.profile
    });
  }

  handleFeedbackClick = () => {
    this.setState({
      visibleModal: Constants.MODAL_FEEDBACK
    });
  }

  handleLoginClick = () => {
    this.setState({
      visibleModal: Constants.MODAL_LOGIN
    });
  }

  handlePlayDailyClick = () => {
    if (this.controlPanel.current) {
      this.controlPanel.current.playTodaysDaily();
    }
  }

  handleProfileClick = () => {
    this.setState({
      visibleModal: Constants.MODAL_PROFILE,
      typographyError: null
    });
  }

  handleLeaderboardButtonClick = () => {
    this.setState({
      visibleModal: Constants.MODAL_LEADERBOARD
    });
  }

  handleRulesButtonClick = () => {
    this.setState({
      visibleModal: Constants.MODAL_RULES
    });
  }

  handleReviewButtonClick = () => {
    this.handleStartButtonClick(
      Constants.GAME_TYPE_DAILY,
      false,
      this.state.loggedInPlayerName,
      Utils.getDateStringToday(),
      Constants.SPECTATOR_PARAMETERS);
  }

  handleLogoutClick = () => {
    return fetch(`/api/account/logout`)
      .then(data => data.json())
      .then(res => {
        this.setState({
          authToken: "",
          liveGame: false,
          gameKey: null
        }, () => {
          this.fetchIdentity();
        });
      });
  }

  handleLoginSubmit = (token) => {
    this.setState({
      authToken: token,
    }, () => {
      this.handleModalClose();
      this.fetchIdentity();
    });
  }

  render() {
    let mainPanel = <ControlPanel
      ref={this.controlPanel}
      playerName={this.state.loggedInPlayerName}
      dailyAttempted={this.state.dailyAttempted}
      dailyScore={this.state.dailyScore}
      sevenDayScoreString={this.state.sevenDayScoreString}
      onLoginClick={this.handleLoginClick}
      onStartButtonClick={this.handleStartButtonClick}
      onContinueButtonClick={this.handleContinueButtonClick}
      onLeaderboardButtonClick={this.handleLeaderboardButtonClick}
      onRulesButtonClick={this.handleRulesButtonClick}
      onReviewButtonClick={this.handleReviewButtonClick}
    />;
    if (this.state.gameKey) {
      mainPanel = <Game
        key={this.state.gameKey}
        gameKey={this.state.gameKey}
        date={this.state.date}
        rows={this.state.rows}
        columns={this.state.columns}
        daily={this.state.daily}
        valid={this.state.dailyAttempted !== Constants.ATTEMPT_COMPLETE}
        expansions={this.state.expansions}
        bonusCells={this.state.bonusCells}
        startLetters={this.state.startLetters}
        holdCount={this.state.holdCount}
        previewCount={this.state.previewCount}
        adjacencyMode={this.state.adjacencyMode}
        incrementMode={this.state.incrementMode}
        dropMode={this.state.dropMode}
        slideMode={this.state.slideMode}
        wraparoundMode={this.state.wraparoundMode}
        playerName={this.state.loggedInPlayerName}
        letterOptionsPerRound={this.state.letterOptionsPerRound}
        letterOptionString={this.state.letterOptionString}
        onGameFinish={this.handleGameFinish}
        onSaveFailure={this.handleSaveFailure}
        onQuitButtonClick={this.handleQuitButtonClick}
        spectating={this.state.spectating}
        boardString={this.state.boardString}
        elapsedTurns={this.state.elapsedTurns}
        holdString={this.state.holdString}
      />;
    }

    const modes = [];
    if (this.state.adjacencyMode) {
      modes.push(Constants.MODE_NAME_ADJACENCY);
    }
    if (this.state.incrementMode) {
      modes.push(Constants.MODE_NAME_INCREMENT);
    }
    if (this.state.dropMode) {
      modes.push(Constants.MODE_NAME_DROP);
    }
    if (this.state.slideMode) {
      modes.push(Constants.MODE_NAME_SLIDE);
    }
    if (this.state.wraparoundMode) {
      modes.push(Constants.MODE_NAME_WRAPAROUND);
    }
    if (modes.length === 0) {
      modes.push(Constants.MODE_NAME_STANDARD);
    }

    return (
      <div className="app">
        <Header
          loggedInPlayerName={this.state.loggedInPlayerName}
          onLoginClick={this.handleLoginClick}
          onLogoutClick={this.handleLogoutClick}
          onProfileClick={this.handleProfileClick}
          streak={this.state.profileStreaks.current}
          streakAtRisk={this.state.dailyAttempted !== Constants.ATTEMPT_COMPLETE}
        />
        <div className="app-main">
          <div className="app-main-left-panel">
            <RulesPanel onScheduleClick={this.handleScheduleClick} />
          </div>
          <div className="app-main-center-panel">
            <DatePanel />
            { this.state.gameKey && 
              <ModePanel
                modes={modes}
              />
            }
            {mainPanel}
          </div>
          <div className="app-main-right-panel">
            <Scoreboard
              clickableToday={this.state.loggedInPlayerName && this.state.dailyAttempted === Constants.ATTEMPT_COMPLETE && !this.state.liveGame}
              loggedInPlayerName={this.state.loggedInPlayerName}
              scores={this.state.scores}
              starMap={this.state.yesterdayStarMap}
              medalMap={this.state.lastWeekMedalMap}
              trophyMap={this.state.lastSeasonTrophyMap}
              onClick={this.handleScoreboardClick}
              onLoginClick={this.handleLoginClick}
              onPlayDailyClick={this.state.gameKey ? null : this.handlePlayDailyClick}
            />
            <div className="app-scoreboard-note">
              Click a player&#39;s name to see their daily board.<br />
              <br />
              You can view today&#39;s boards and leaderboards only if you&#39;re logged in and have played the daily.<br />
              <br />
              { Seasons.hasStarted(Utils.getDateStringToday())
                ? <>
                    Stars are awarded to the previous day's top scorers, medals for the previous week's, and trophies for the previous season's.<br />
                    <br />
                    Click your username in the header to see your accolades and past season finishes!<br />
                    <br />
                    Hover over a player&#39;s weekly score to see their individual scores for that week, or over a season score to see the sum of their best five scores for each day.
                  </>
                : <>
                    Stars are awarded to the previous day's top scorers, and trophies are awarded for the previous week's top scorers.<br />
                    <br />
                    Click your username in the header to see your star counts!<br />
                    <br />
                    Hover over a player&#39;s weekly score to see their individual scores for that week.
                  </>
              }
            </div>
          </div>
        </div>
        <Footer onFeedbackClick={this.handleFeedbackClick} />

        { this.state.visibleModal === Constants.MODAL_SCHEDULE &&
          <ScheduleModal onClose={this.handleModalClose} />
        }
        { this.state.visibleModal === Constants.MODAL_SEASON_WELCOME &&
          <SeasonWelcomeModal
            announcement={this.state.announcement}
            loggedInPlayerName={this.state.loggedInPlayerName}
            onClose={this.handleModalClose}
          />
        }
        { this.state.visibleModal === Constants.MODAL_ANNOUNCEMENT &&
          <AnnouncementModal announcement={this.state.announcement} onClose={this.handleModalClose} />
        }
        { this.state.visibleModal === Constants.MODAL_FEEDBACK &&
          <FeedbackModal onClose={this.handleModalClose} />
        }
        { this.state.visibleModal === Constants.MODAL_LOGIN &&
          <LoginModal onSubmit={this.handleLoginSubmit} onCancel={this.handleModalClose} />
        }
        { this.state.visibleModal === Constants.MODAL_PROFILE &&
          <ProfileModal
            profile={this.state.profile}
            seasons={this.state.profileSeasons}
            medals={this.state.profileMedals}
            streaks={this.state.profileStreaks}
            streakAtRisk={this.state.dailyAttempted !== Constants.ATTEMPT_COMPLETE}
            typography={this.state.typography}
            typographyError={this.state.typographyError}
            onTypographyChange={this.handleTypographyChange}
            onClose={this.handleModalClose}
          />
        }
        { this.state.visibleModal === Constants.MODAL_LEADERBOARD &&
          <LeaderboardModal
            clickableToday={this.state.loggedInPlayerName && this.state.dailyAttempted === Constants.ATTEMPT_COMPLETE && !this.state.liveGame}
            loggedInPlayerName={this.state.loggedInPlayerName}
            scores={this.state.scores}
            yesterdayStarMap={this.state.yesterdayStarMap}
            lastWeekMedalMap={this.state.lastWeekMedalMap}
            lastSeasonTrophyMap={this.state.lastSeasonTrophyMap}
            onClick={this.handleScoreboardClick}
            onLoginClick={this.handleLoginClick}
            onPlayDailyClick={this.state.gameKey ? null : this.handlePlayDailyClick}
            onClose={this.handleModalClose}
          />
        }
        { this.state.visibleModal === Constants.MODAL_RULES &&
          <RulesModal onScheduleClick={this.handleScheduleClick} onClose={this.handleModalClose}/>
        }
      </div>
    );
  }
}

export default App;
